import { Button, EmptyState, Icon, Tag } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';

import { ErrorState } from 'src/core/common/components/ErrorState';
import { useParams } from 'src/core/common/hooks/useParams';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import history from 'src/core/history';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { useUser } from 'src/core/modules/app/hooks/useUser';
import { getTripTitle } from 'src/core/modules/requests/utils/trips';
import { type TripDetails } from 'src/core/modules/trips/hooks/useTripDetailsQuery';
import { UserAvatar } from 'src/core/modules/users/components/UserAvatar';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';
import { logger } from 'src/utils/datadog-log-wrapper';

import { BookingConfirmationPageSkeleton } from './BookingConfirmationPageSkeleton';
import { TravelPageWrapper } from '../../components/TravelPageWrapper/TravelPageWrapper';
import { usePayTripBookings } from '../../hooks/usePayTripBookings';
import { usePaymentStatusQuery } from '../../hooks/usePaymentStatusQuery';
import { useTripDetailsQuery } from '../../hooks/useTripDetailsQuery';

export const BookingConfirmationPage = () => {
  const { id: tripId } = useParams(routes.TRAVEL_BOOKING_CONFIRMATION.path);
  const companyId = useCompanyId();
  const { t } = useTranslation('global');
  const user = useUser();
  const { data: tripDetails, isError: isTripError, isLoading: isTripLoading } =
    useTripDetailsQuery(tripId);
  const payTripBookingsMutation = usePayTripBookings();
  const [tripIdToPoll, setTripIdToPoll] = useState<string | null>(null);
  const paymentStatusQuery = usePaymentStatusQuery(tripIdToPoll);
  const isPaying =
    paymentStatusQuery.isLoading ||
    paymentStatusQuery.isFetching ||
    payTripBookingsMutation.isLoading ||
    Boolean(tripIdToPoll);

  const pendingBookings =
    tripDetails?.bookings?.filter(
      (booking) => booking.status === 'pendingPayment',
    ) ?? [];

  const totalPrice =
    pendingBookings.length > 0
      ? pendingBookings.reduce(
          (sum, booking) => sum + booking.amount.totalPrice.amount,
          0,
        )
      : 0;

  const handleBack = () => {
    if (tripId) {
      history.push(
        routeFor(routes.TRAVEL_TRIP_DETAILS.path, {
          company: companyId,
          id: tripId,
        }),
      );
    } else {
      history.push(
        routeFor(routes.TRAVEL_SEARCH.path, {
          company: companyId,
        }),
      );
    }
  };

  const handleBookAndPay = async () => {
    try {
      await payTripBookingsMutation.mutateAsync(tripId);
      setTripIdToPoll(tripId);
    } catch (error) {
      logger.error('Failed to pay for bookings:', {
        error,
        scope: 'travel-booking-confirmation',
        team: 'travel-and-expenses',
      });
    }
  };

  useEffect(() => {
    if (paymentStatusQuery.isPaymentSuccessful) {
      setTripIdToPoll(null);
      history.push(
        routeFor(routes.TRAVEL_TRIP_DETAILS.path, {
          company: companyId,
          id: tripId,
        }),
      );
    }
  }, [paymentStatusQuery.paymentStatus]);

  if (isTripError) {
    return (
      <TravelPageWrapper
        onBack={handleBack}
        title={t('travel.tripDetails.header.title')}
      >
        <ErrorState
          title={t('misc.errorState.title')}
          description={t('misc.errorState.subtitle')}
        />
      </TravelPageWrapper>
    );
  }

  if (isTripLoading) {
    return (
      <BookingConfirmationPageSkeleton />
    );
  }

  const tripTitle =
    getTripTitle(tripDetails?.route ?? [], t) ??
    t('travel.tripDetails.header.title');

  if (pendingBookings.length === 0) {
    return (
      <TravelPageWrapper onBack={handleBack} title={tripTitle}>
        <div className="flex items-center justify-center py-80">
          <EmptyState
            title={t('travel.tripDetails.noBookings.title')}
            subtitle={t('travel.tripDetails.noBookings.description')}
            iconName="bed"
            iconVariant="neutral"
            actions={
              <Button
                variant="secondaryNeutral"
                text={t('travel.tripDetails.noBookings.cta')}
                onClick={handleBack}
              />
            }
          />
        </div>
      </TravelPageWrapper>
    );
  }

  return (
    <TravelPageWrapper onBack={handleBack} title={tripTitle}>
      <div className="relative flex flex-1 gap-56 px-40 pt-24">
        <div className="flex flex-[2] flex-col gap-24 pb-32">
          <div className="box flex flex-col gap-8 p-8">
            <div className="flex items-center gap-8 p-8">
              <p className="title-l">
                {t('travel.tripDetails.bookingDetails.traveller')}
              </p>
            </div>
            <div className="bg-background-secondary-default rounded-8 p-16">
              <div className="flex items-center gap-8">
                <UserAvatar
                  id={user.id}
                  name={user.fullname || user.email}
                  avatar={user.avatar ?? undefined}
                  size={32}
                />
                <p className="title-l">{user.fullname || user.email}</p>
              </div>
            </div>
          </div>

          <div className="box flex flex-col gap-16 p-16">
            <div className="flex items-center gap-8">
              <p className="title-l">
                {t('travel.tripDetails.bookingDetails.paymentMethod')}
              </p>
            </div>
            <div className="flex items-baseline justify-between gap-8">
              <div className="flex items-center gap-8">
                <Tag variant="info">
                  {t('travel.tripDetails.bookingDetails.betaOnly')}
                </Tag>
                <p className="body-m">
                  {t('travel.tripDetails.bookingDetails.duffelAccountBalance')}
                </p>
              </div>
              {tripDetails && <RemainingBudget trip={tripDetails} />}
            </div>
          </div>
        </div>

        <div className="box flex flex-1 flex-col gap-16 self-baseline overflow-hidden p-16">
          <p className="title-l">
            {t('travel.tripDetails.bookingDetails.priceBreakdown')}
          </p>

          <div className="flex flex-col gap-8">
            {pendingBookings.map((booking) => (
              <div
                key={booking.id}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-4">
                  <Icon name="bed" size="s" />
                  <p className="body-m">{booking.property.name}</p>
                </div>
                <p className="title-m text-right">
                  {formatMonetaryValue(booking.amount.totalPrice)}
                </p>
              </div>
            ))}
          </div>

          <div className="separator" />

          <div className="flex items-baseline justify-between gap-4">
            <p className="title-l">
              {t('travel.tripDetails.bookingDetails.total')}
            </p>
            <p className="title-xl text-right">
              {formatMonetaryValue({
                amount: totalPrice,
                currency:
                  pendingBookings[0]?.amount.totalPrice.currency ?? 'EUR',
                precision: pendingBookings[0]?.amount.totalPrice.precision ?? 2,
              })}
            </p>
          </div>

          <div className="flex flex-col items-end justify-end">
            <Button
              variant="primaryBrand"
              text={t('travel.tripDetails.bookingDetails.bookAndPay')}
              onClick={handleBookAndPay}
              fit="parent"
              isLoading={isPaying}
            />
          </div>
        </div>
      </div>
    </TravelPageWrapper>
  );
};

const RemainingBudget = ({ trip }: { trip: TripDetails }) => {
  const { t } = useTranslation('global');
  const remainingBudget = trip.budget?.state?.available
    ? formatMonetaryValue(trip.budget.state.available)
    : null;
  const totalBudget = trip.budget?.state?.budgeted
    ? formatMonetaryValue(trip.budget.state.budgeted)
    : null;
  return (
    <p className="body-m text-content-secondary-bg-primary">
      {t('travel.tripDetails.bookingDetails.remainingTripBudget')}:{' '}
      {remainingBudget}/{totalBudget}
    </p>
  );
};
